import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {DocumentIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {DownloadIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {Document} from "types/Document";
import ConfirmModal from "components/ConfirmModal";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {useOperator} from "context/OperatorContext";

function DocumentList() {
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['documents.all', {page}], () => ngage.documents.all(page), {keepPreviousData: true})

    const [deletingDocument, setDeletingDocument] = useState<Document | null>(null);
    const {mutateAsync: deleteDocument} = useMutation(ngage.documents.destroy)

    const {subdomain} = useOperator()
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prepare action handlers
    async function handleDeleteDocument(document: Document) {
        if(document.id){
            try{
                await deleteDocument(document.id)

                setDeletingDocument(null)
                notify("success", "Document Deleted", `${document.name} has been permanently removed`)

                return invalidate('documents.all')
            } catch (error) {
                setDeletingDocument(null)
                notify("error", "Document Not Deleted", `${document.name} could not be removed`)
            }
        }
    }

    // Prefetch next and previous pages
    prefetchNearbyPages('documents.all', page, ngage.documents.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/documents/new')}>New</Button>

    const breadcrumbs = {'Admin': '/', 'Documents': '/users'}

    return (
        <Page title="Documents" actions={actions} breadcrumbs={breadcrumbs}>
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader>Owner</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={5}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <DocumentIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No documents</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((document, index) => (
                        <TableRow key={document.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/documents/${document.id}`)},
                                                {name: 'Download', icon: DownloadIcon, url: ngage.documents.downloadUrl(document.id, subdomain), download: true},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/documents/${document.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingDocument(document)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <DocumentIcon className="h-5 w-5 mr-2 text-gray-500" /> {truncate(document.name, 35)}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(document.description, 50)}</TableData>
                            <TableData>{document.owner.name}</TableData>
                            <TableData>{formattedDateTime(document.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/documents/${document.id}`)},
                                        {name: 'Download', icon: DownloadIcon, url: ngage.documents.downloadUrl(document.id, subdomain), download: true},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/documents/${document.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingDocument(document)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete modal */}
                            <ConfirmModal
                                show={deletingDocument === document}
                                type={ConfirmModal.types.DANGER}
                                title="Delete Document"
                                body={`Are you sure you want to permanently delete '${document.name}'? This action cannot be undone.`}
                                approveLabel="Delete"
                                onApprove={() => handleDeleteDocument(document)}
                                onCancel={() => setDeletingDocument(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default DocumentList;